/* global URL_API_REST */
/**
 * PRD-037 — Cliente del backend del partner (panel-comercio).
 *
 * Usa una instancia axios DEDICADA sin el interceptor de auth de axiosInstance: las rutas
 * `/v1/partner/*` son Auth NONE en API Gateway y un header `Authorization` las rompe (API GW
 * lo intercepta como SigV4). El token de partner viaja en el header `partner-token`.
 * Las rutas `/v1/claim/*` son públicas (sin token).
 */
import axios from 'axios';
import type { UserType } from '@interfaces/general';

const partnerApi = axios.create({
  baseURL: URL_API_REST,
  timeout: 30 * 1000,
});

export interface PartnerInfo {
  id: string;
  usuario: string;
  nombre: string;
  modulos: string[];
}

export interface PartnerSession {
  token: string;
  partner: PartnerInfo;
}

export interface PartnerCommerce {
  id_suscripcion: string;
  id_comercio: string | null;
  partner_id: string;
  claimed_at: string | null;
  tipo_plan: string | null;
  id_cliente: string | null;
  estado: 'draft' | 'reclamado';
  negocio_nombre: string | null;
  owner_nombre: string | null;
  owner_correo: string | null;
  owner_telefono: string | null;
  created_at: string | null;
}

export interface DraftResult {
  draft: boolean;
  id_comercio: string;
  id_suscripcion: string;
  id_cliente: string;
  id_usuario_draft: string;
  partner_id: string;
  claimed_at: string | null;
  id_path: string;
}

export interface DraftSessionResult {
  jwt: string;
  id_comercio: string;
  id_usuario: string;
}

export interface ClaimLinkResult {
  token: string;
  claim_url: string;
  expires_at: number;
  ttl_seconds: number;
}

export interface ClaimOpenResult {
  negocio: {
    id_comercio: string;
    nombre?: string;
    direccion?: string;
    nombre_contacto?: string;
    telefono_contacto?: string;
    correo_contacto?: string;
  };
  menu_preview: { categorias: Array<{ id: string; nombre: string }> };
  otp_sent: boolean;
  phone: string | null;
}

// Payload listo para AuthContext.login() (sesión plena del comercio reclamado).
export interface CommerceSession {
  id_commerce: string;
  id_user: string;
  id_path: string;
  commerce_name: string;
  commerce_logo: string;
  user_name: string;
  token: string;
  user: { email: string; username: string; name: string; status: string };
  onboarding_finish_date: string | null;
  user_type: UserType;
  is_root: boolean;
}

export interface ClaimCompleteResult {
  jwt: string;
  id_comercio: string;
  id_usuario: string;
  claimed_at: string | null;
  session: CommerceSession;
}

export interface ClaimResendResult {
  otp_sent: boolean;
  phone: string | null;
}

const withToken = (partnerToken: string) => ({ headers: { 'partner-token': partnerToken } });

export const partnerLogin = async (usuario: string, password: string): Promise<PartnerSession> => {
  const { data } = await partnerApi.post<PartnerSession>('/v1/partner/login', { usuario, password });
  return data;
};

export const createDraft = async (
  partnerToken: string,
  body: {
    negocio: { nombre: string; direccion?: string; giro?: string; id_path?: string };
    owner: { nombre: string; email: string; whatsapp?: string; telefono?: string };
    monthly_orders?: string;
    product_interest?: string[];
  },
): Promise<DraftResult> => {
  const { data } = await partnerApi.post<DraftResult>('/v1/partner/commerces', body, withToken(partnerToken));
  return data;
};

export const listPartnerCommerces = async (partnerToken: string): Promise<PartnerCommerce[]> => {
  const { data } = await partnerApi.get<{ commerces: PartnerCommerce[] }>(
    '/v1/partner/commerces',
    withToken(partnerToken),
  );
  return data.commerces;
};

export const createDraftSession = async (partnerToken: string, idComercio: string): Promise<DraftSessionResult> => {
  const { data } = await partnerApi.post<DraftSessionResult>(
    '/v1/partner/draft-session',
    { id_comercio: idComercio },
    withToken(partnerToken),
  );
  return data;
};

export const createClaimLink = async (partnerToken: string, idComercio: string): Promise<ClaimLinkResult> => {
  const { data } = await partnerApi.post<ClaimLinkResult>(
    `/v1/partner/commerces/${idComercio}/claim-link`,
    {},
    withToken(partnerToken),
  );
  return data;
};

export const claimOpen = async (token: string): Promise<ClaimOpenResult> => {
  const { data } = await partnerApi.post<ClaimOpenResult>('/v1/claim/open', { token });
  return data;
};

// Reenvia el OTP del claim. Reusa el mismo mecanismo que el registro normal (el send reusa el
// codigo activo si existe). El backend deriva el telefono del comercio server-side.
export const claimResendOtp = async (token: string): Promise<ClaimResendResult> => {
  const { data } = await partnerApi.post<ClaimResendResult>('/v1/claim/resend-otp', { token });
  return data;
};

export const claimComplete = async (body: {
  token: string;
  phone: string;
  otp_code: string;
  usuario: string;
  password: string;
  nombre?: string;
  correo?: string;
  acepta_terminos?: boolean;
}): Promise<ClaimCompleteResult> => {
  const { data } = await partnerApi.post<ClaimCompleteResult>('/v1/claim/complete', body);
  return data;
};
